import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Filter, Plus, Search } from "lucide-react";

import { AdminLayout } from "@/components/admin/layout/AdminLayout";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { blogAdminService } from "@/services/blogAdminService";
import { PostTable } from "@/components/admin/blog/PostTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { BlogPost } from "@/types/blog";

export default function AdminPosts() {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAdminAuth();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const publishedCount = useMemo(() => posts.filter(post => post.published).length, [posts]);
  const draftCount = useMemo(() => posts.filter(post => !post.published).length, [posts]);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate("/admin-login");
    }
  }, [authLoading, isAdmin, navigate]);

  useEffect(() => {
    loadPosts();
  }, [search, statusFilter, page]);

  async function loadPosts() {
    try {
      setLoading(true);
      const result = await blogAdminService.listPosts({
        search,
        status: statusFilter,
        page,
        limit: 20,
      });
      setPosts(result.posts);
      setTotalPages(result.totalPages);
    } catch (error: any) {
      toast.error(error.message || "Erro ao carregar posts");
    } finally {
      setLoading(false);
    }
  }

  async function handlePublishToggle(id: string, published: boolean) {
    try {
      await blogAdminService.publishPost(id, published);
      toast.success(published ? "Post publicado!" : "Post despublicado!");
      loadPosts();
    } catch (error: any) {
      toast.error(error.message || "Erro ao atualizar status");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Tem certeza que deseja excluir este post?")) return;

    try {
      await blogAdminService.deletePost(id);
      toast.success("Post excluído com sucesso!");
      loadPosts();
    } catch (error: any) {
      toast.error(error.message || "Erro ao excluir post");
    }
  }

  if (authLoading || !isAdmin) {
    return null;
  }

  return (
    <AdminLayout
      title="Operações do Blog"
      description="Controle de publicações, status e distribuição do conteúdo editorial Toc Toc."
      breadcrumbs={[
        { label: "Admin", href: "/admin" },
        { label: "Conteúdo" },
        { label: "Blog" },
      ]}
      actions={
        <Button asChild>
          <Link to="/admin/posts/new">
            <Plus className="mr-2 h-4 w-4" />
            Novo Post
          </Link>
        </Button>
      }
    >
      <section className="grid gap-6 lg:grid-cols-[1fr_0.6fr]">
        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-lg font-semibold text-slate-900">Painel de publicação</CardTitle>
              <CardDescription className="text-xs text-slate-500">
                Use filtros inteligentes para localizar, revisar e publicar histórias.
              </CardDescription>
            </div>
            <Badge className="bg-emerald-50 text-xs text-emerald-600">{posts.length} itens</Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar por título, categoria ou tag..."
                  value={search}
                  onChange={event => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  className="border-slate-200 bg-white pl-10 text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={(value: any) => {
                  setStatusFilter(value);
                  setPage(1);
                }}
              >
                <SelectTrigger className="w-full border-slate-200 bg-white text-slate-900 lg:w-48">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="published">Publicados</SelectItem>
                  <SelectItem value="draft">Rascunhos</SelectItem>
                </SelectContent>
              </Select>
            </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600">
                <div className="flex flex-wrap items-center gap-4">
                  <span className="inline-flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    <strong className="text-slate-900">Status atual</strong>
                  </span>
                  <span>{publishedCount} publicados</span>
                  <span>{draftCount} rascunhos</span>
                  <span>{posts.length} posts no total</span>
                </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-sm text-slate-500">
                Carregando posts...
              </div>
            ) : (
              <PostTable
                posts={posts}
                onPublishToggle={handlePublishToggle}
                onDelete={handleDelete}
              />
            )}

            {!loading && totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 pt-4 text-slate-600 sm:flex-row">
                <span className="text-xs">Página {page} de {totalPages}</span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:border-[#FF5605] hover:bg-[#FF5605]/10"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    className="border-slate-200 text-slate-700 hover:border-[#FF5605] hover:bg-[#FF5605]/10"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 bg-white text-slate-900 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Resumo editorial</CardTitle>
            <CardDescription className="text-xs text-slate-500">
              Indicadores rápidos para orientar os próximos conteúdos.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-xs text-slate-600">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="text-sm font-semibold text-slate-900">Ritmo de publicação</h4>
              <p className="mt-1">Recomende ao time manter dois novos posts semanais para sustentar o crescimento orgânico.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="text-sm font-semibold text-slate-900">Rascunhos prioritários</h4>
              <p className="mt-1">Utilize o filtro "Rascunhos" para revisar conteúdos pendentes de aprovação.</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h4 className="text-sm font-semibold text-slate-900">Distribuição automática</h4>
              <p className="mt-1">Posts publicados são sincronizados com o CRM Toc Toc e com a newsletter semanal.</p>
            </div>
            <Button variant="link" className="px-0 text-[#3332FC] hover:text-[#FF5605]" asChild>
              <Link to="/admin/health">Conferir status das integrações →</Link>
            </Button>
          </CardContent>
        </Card>
      </section>
    </AdminLayout>
  );
}
